import {ReactElement, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import cx from 'classnames';
import {useDispatch, useSelector} from 'react-redux';
import {useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {ResponsiveModal} from '~library/organisms/ResponsiveModal';
import {BottomSheet} from '~library/molecules/BottomSheet';
import {getUserData} from '~library/utils/auth/utils';
import {
  selectHasMultipleRegistries,
  selectHasRegistries,
} from '~/common/fabric/reducers/registry/selectors';
import {doesMatchEventCategory} from '~/common/utils/event_utils';
import {FUNERAL_CATEGORY} from '~/common/utils/event_constants';
import {selectClassifiedCategory} from '~/common/fabric/reducers/eviteEvent/selectors';
import {RSVPConfirmed} from '~/ViewInvitation/common/RSVPModalVariantAB/steps/RSVPConfirmed/RSVPConfirmed';
import {
  selectEmailMd5,
  selectEmailSha256,
  selectHasRsvpd,
  selectIsFirstRsvpRender,
} from '~/ViewInvitation/selectors/rsvp';
import {SET_FIRST_RSVP_RENDER} from '~/ViewInvitation/reducers/constants';
import {
  selectEventTemplateName,
  selectIsFuneral,
  selectShowGifting,
} from '~/ViewInvitation/selectors/eviteEvent';
import {Animation} from '~/ViewInvitation/common/RSVPModalVariantAB/steps/components/ConfettiAnimation';
import {RSVPModalProps} from '~/ViewInvitation/common/RSVPModalTestAB';
import {useSuccessPageRokt} from '~/ViewInvitation/common/RSVPModalVariantAB/steps/SuccessRokt/useSuccessPageRokt';
import {CollectInfo} from './steps/CollectInfo/CollectInfo';
import {Payload} from './types';
import {SuccessRokt} from './steps/SuccessRokt/SuccessRokt';
import {RegistryWishlist} from './steps/RegistryWishlist/RegistryWishlist';
import {CecPreferences, ChuckECheese} from './steps/ChuckECheese/ChuckECheese';
import {GiftIdeas} from './steps/GiftIdeas/GiftIdeas';

import './RSVPModalVariantAB.sass';

export const RSVPModalVariantAB = ({open, status, onClose, onSubmitRSVP}: RSVPModalProps) => {
  const dispatch = useDispatch();
  const isMediumAndUpScreen = useIsMediumAndUp();
  const hasRegistries = useSelector(selectHasRegistries);
  const hasMultipleRegistries = useSelector(selectHasMultipleRegistries);
  const templateName = useSelector(selectEventTemplateName);
  const showGifting = useSelector(selectShowGifting);
  const [step, setStep] = useState(0);
  const isFirstRender = useSelector(selectIsFirstRsvpRender);
  const [stepsCounter, setStepsCounter] = useState(0);
  const {initRokt, hasOffers, initialized, clearRoktInstance} = useSuccessPageRokt();
  const [payload, setPayload] = useState({} as Payload);
  const isFuneralType = useSelector(selectIsFuneral);
  const classifiedCategory = useSelector(selectClassifiedCategory);
  const initRoktTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Setting these refs to avoid stale values when calling initRokt()
  const hasRsvpd = useSelector(selectHasRsvpd);
  const hasRsvpdRef = useRef(hasRsvpd);
  const emailMd5 = useSelector(selectEmailMd5);
  const emailMd5Ref = useRef(emailMd5);
  const emailSha256 = useSelector(selectEmailSha256);
  const emailSha256Ref = useRef(emailSha256);

  const isFuneral = isFuneralType || doesMatchEventCategory(classifiedCategory, FUNERAL_CATEGORY);
  const userData = getUserData();
  const searchParams = new URLSearchParams(window.location.search);
  const gid = searchParams.get('gid');
  const guestId = userData.guest_id || userData.guestId || gid || window.guest_id;

  const onClickCloseHandler = useCallback(async () => {
    await clearRoktInstance();
    setStep(0);
    onClose();
  }, [clearRoktInstance, onClose]);

  // This will inject the user ID into the RSVP payload
  const onSubmitHandler = useCallback(
    (info: Payload) =>
      onSubmitRSVP(
        {
          ...info,
          guest_id: guestId,
        },
        true
      ),
    [guestId, onSubmitRSVP]
  );

  const saveRSVP = useCallback(
    async (info: Payload) => {
      setPayload(info);
      return onSubmitHandler(info);
    },
    [onSubmitHandler]
  );

  useEffect(() => {
    document.body.classList.toggle('no-scroll', open);

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [open]);

  const onGoForwardClick = useCallback(() => {
    const nextStep = step + 1;
    if (nextStep > stepsCounter) {
      onClose();
    } else {
      setStep(nextStep);
    }
  }, [step, stepsCounter, onClose]);

  useEffect(() => {
    if (hasRsvpd && emailMd5 && emailSha256) {
      hasRsvpdRef.current = hasRsvpd;
      emailMd5Ref.current = emailMd5;
      emailSha256Ref.current = emailSha256;
    }
  }, [hasRsvpd, emailMd5, emailSha256]);

  // Clear Timout on unmount
  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      if (initRoktTimeoutId.current) {
        clearTimeout(initRoktTimeoutId.current);
      }
    };
  }, []);

  // Initialize ROKT
  const initRoktCb = useCallback(async () => {
    // Wait until hasRsvpdRef.current becomes true
    await new Promise<void>((resolve) => {
      const check = () => {
        if (hasRsvpdRef.current && emailMd5Ref.current && emailSha256Ref.current) {
          resolve();
        } else {
          initRoktTimeoutId.current = setTimeout(check, 100);
        }
      };
      check();
    });

    // Once hasRsvpdRef.current is true, initialize ROKT
    if (!initialized) {
      await initRokt({
        emailMd5: emailMd5Ref.current,
        emailSha256: emailSha256Ref.current,
      });
    }
  }, [initialized, initRokt]);

  // If ROKT is initialized & is first render of RSVP modal, go to next step
  useEffect(() => {
    if (initialized && isFirstRender) {
      onGoForwardClick();
      dispatch({type: SET_FIRST_RSVP_RENDER, payload: false});
    }
  }, [dispatch, onGoForwardClick, initialized, isFirstRender]);

  const onCecSave = useCallback(
    async (newInfo: CecPreferences) => {
      await onSubmitHandler({...payload, ...newInfo});
      onGoForwardClick();
    },
    [onGoForwardClick, onSubmitHandler, payload]
  );

  const storeInfoHandler = useCallback(
    async (info: Payload) => {
      try {
        // Save RSVP and init rokt if needed, else go forward
        await saveRSVP(info);
        await initRoktCb();
        // On additional renders, initialized is already true so just await save & go to next step
        if (!isFirstRender) {
          onGoForwardClick();
        }
      } catch (error) {
        evite.error(error);
      }
    },
    [saveRSVP, initRoktCb, onGoForwardClick, isFirstRender]
  );

  const steps = useMemo(() => {
    const hasRoktOffers = initialized && hasOffers;
    const isChuckECheese = templateName.includes('chuckecheese');
    const stepsList: ReactElement[] = [];

    // default
    stepsList.push(
      <CollectInfo
        key="collect-info"
        status={status}
        onCtaClick={storeInfoHandler}
        onClickClose={onClickCloseHandler}
      />
    );

    if (isChuckECheese) {
      stepsList.push(
        <ChuckECheese key="chuck-e-cheese" onCtaClick={onCecSave} onCloseClick={onGoForwardClick} />
      );
    }

    if (isFuneral) {
      if (hasRoktOffers) {
        stepsList.push(
          <SuccessRokt
            key="success-rokt-funeral"
            onPlacementClosed={onClickCloseHandler}
            onClose={onClickCloseHandler}
          />
        );
      } else {
        stepsList.push(
          <RSVPConfirmed
            key="rsvp-confirmed-funeral"
            isFuneral={isFuneral}
            onCloseClick={onClickCloseHandler}
          />
        );
      }
    } else if (hasRegistries) {
      if (hasRoktOffers) {
        stepsList.push(
          <RegistryWishlist key="registry-wishlist" onCloseClick={onGoForwardClick} />
        );
        stepsList.push(
          <SuccessRokt
            key="success-rokt-registry"
            onPlacementClosed={onClickCloseHandler}
            onClose={onClickCloseHandler}
            onUnmountAnchorTo={hasMultipleRegistries ? '.submodule-registry' : undefined}
          />
        );
      } else {
        stepsList.push(
          <RegistryWishlist key="registry-wishlist-no-rokt" onCloseClick={onClickCloseHandler} />
        );
      }
    } else if (hasRoktOffers) {
      if (showGifting) {
        stepsList.push(
          <SuccessRokt
            key="success-rokt-gifting"
            onPlacementClosed={onGoForwardClick}
            onNext={onGoForwardClick}
            showSkipButton
          />
        );
        stepsList.push(<GiftIdeas key="gift-ideas" onCloseClick={onClickCloseHandler} />);
      } else {
        stepsList.push(
          <SuccessRokt
            key="success-rokt-no-gifting"
            onPlacementClosed={onClickCloseHandler}
            onClose={onClickCloseHandler}
          />
        );
      }
    } else if (showGifting) {
      stepsList.push(<GiftIdeas key="gift-ideas-no-rokt" onCloseClick={onClickCloseHandler} />);
    } else {
      stepsList.push(<RSVPConfirmed key="rsvp-confirmed" onCloseClick={onClickCloseHandler} />);
    }

    setStepsCounter(stepsList.length);

    return stepsList;
  }, [
    initialized,
    hasOffers,
    templateName,
    status,
    storeInfoHandler,
    onClickCloseHandler,
    hasRegistries,
    isFuneral,
    onCecSave,
    onGoForwardClick,
    hasMultipleRegistries,
    showGifting,
  ]);

  const renderModalContent = useCallback(
    () => (
      <>
        <Animation status={status} step={step} />
        {steps.map((stepComponent, index) => (
          <div
            key={stepComponent.key || `step-${index}`}
            className={cx('RSVP__Modal__content', {
              'RSVP__Modal__content--active': index === step,
            })}
          >
            {stepComponent}
          </div>
        ))}
      </>
    ),
    [status, step, steps]
  );

  if (isMediumAndUpScreen) {
    return (
      <ResponsiveModal
        isModal
        isOpen={open}
        onClose={() => null}
        overlayClassName="RSVP__Modal"
        showHeader={false}
      >
        {renderModalContent()}
      </ResponsiveModal>
    );
  }

  return (
    <BottomSheet
      in={open}
      className="custom-bottom-sheet__overlay__content"
      content={renderModalContent()}
    />
  );
};
